import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

const CustomizedComponent = styled(TextField)(({ theme }) => ({
  width: "100%",
  color: theme.palette.commonground.contrastText,
  transform: "scale(1)",
  "& .MuiInputBase-root:not(.Mui-disabled)": {
    "&:hover": {
      "& > fieldset": {
        borderColor: alpha(theme.palette.secondary.main, 0.75),
      },
    },
    "&.Mui-focused fieldset": {
      borderColor: alpha(theme.palette.secondary.main, 0.75),
    },
    "& .MuiIconButton-edgeEnd": {
      marginRight: `-${theme.spacing(1)}`,
    },
    "& .MuiIconButton-root, .MuiSvgIcon-root": {
      "&:hover": {
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.light,
        transform: "scale(1.075)",
      },
      "&:active": {
        transform: "scale(0.98)",
      },
    },
  },
}));

// A URL is canonical when it carries an explicit http:// or https:// scheme.
// Empty values are treated as valid so the field is not in an error state until
// the user actually types a non-canonical value.
export const isCanonicalUrl = value => {
  if (value == null || value.toString().trim() === "") {
    return true;
  }

  try {
    const parsedUrl = new URL(value.toString().trim());

    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch {
    return false;
  }
};

// A relative site path such as "/products/heloc".
const isRelativePath = value => value != null && value.toString().trim().startsWith("/");

// Whether a value is acceptable for the field, honoring `allowRelative`.
export const isAcceptableUrl = (value, allowRelative = false) =>
  isCanonicalUrl(value) || (allowRelative && isRelativePath(value));

const OmniUrlField = props => {
  const {
    children,
    color = "secondary",
    onChange = null,
    label = "Missing",
    type = "url",
    size = "small",
    variant = "outlined",
    error = false,
    helperText = "",
    showOpenButton = true,
    allowRelative = false,
    value,
    ...other
  } = props;

  const urlIsValid = isAcceptableUrl(value, allowRelative);
  const hasError = error || !urlIsValid;
  const resolvedHelperText = !urlIsValid
    ? allowRelative
      ? "Enter a full URL (http://…) or a site path beginning with /."
      : "Enter a canonical URL beginning with http:// or https://."
    : helperText;

  // The launch button opens external links; relative paths aren't openable here.
  const canOpen = isCanonicalUrl(value) && value != null && value.toString().trim() !== "";

  return (
    <CustomizedComponent
      autoComplete="off"
      //prevents AutoComplete Suggestions
      label={label}
      value={value}
      variant={variant}
      color={color}
      margin="dense"
      type={type}
      size={size}
      error={hasError}
      helperText={resolvedHelperText}
      inputProps={{
        autoComplete: "off",
      }}
      InputLabelProps={{
        shrink: true,
      }}
      InputProps={{
        endAdornment: showOpenButton ? (
          <InputAdornment position="end">
            <Tooltip
              title={canOpen ? "Open link in a new tab" : "Enter a canonical URL to open it"}
            >
              <span>
                <IconButton
                  edge="end"
                  size="small"
                  tabIndex={-1}
                  disabled={!canOpen}
                  onClick={() => {
                    if (canOpen) {
                      window.open(value.toString().trim(), "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ) : null,
      }}
      onChange={event => {
        if (onChange != null) {
          onChange(event);
        }
      }}
      {...other}
    >
      {children}
    </CustomizedComponent>
  );
};

export default OmniUrlField;
